import { ChainIdEnum } from 'config/constants/network'
import { Token } from 'config/types'
import { useAnalytics } from 'hooks/useAnalytics'
import useFullSizeModalRouteUpdater from 'hooks/useFullSizeModalRouteUpdater'
import useModal from 'hooks/useModal'
import Column from 'layout/Components/Column'
import React, { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'
import { useTokenInfo } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import { useUserBonus } from 'state/bonus/hooks/useUserBonus'
import { ModalBody, ModalHeader } from 'UIKit/AlchemyModal'
import { ModalSizeModeEnum } from 'UIKit/AlchemyModal/Modal'
import { ModalProps } from 'UIKit/ModalV2'
import { useWaitingDepositToActiveBonus } from 'views/Payment/components/Deposit/DepositCryptos/hooks'
import { PaymentPageEnums } from 'views/Payment/Menu'
import FiatCurrencySelectModal from '../../components/FiatCurrencySelect'
import { PaymentHistoryButton } from '../../components/PaymentHistoryModal'
import SelectedCurrencyItem from '../../components/SelectedCurrencyItem'
import {
  PAYMENT_MODAL_THEME,
  PaymentCurrencyBlockMessage,
  PaymentLabel,
  StylePaymentModal,
} from '../../components/styles'
import { DepositFiatControls, DepositModeEnums, useDepositControl } from '../../hooks/useDeposit'
import DepositFiatForm from './DepositFiatForm'

const DepositFiat: React.FC<
  ModalProps<{
    initialToken?: Token
    isFiatCurrency?: boolean
  }>
> = ({ data, onDismiss }) => {
  const { isSigned } = useAuth()
  const { initialToken } = data
  const { addMetaEvent } = useAnalytics()
  const { fetchBonuses } = useUserBonus()

  const [selectedToken, setSelectedToken] = useState<Token>(data.initialToken || null)

  const onBack = useFullSizeModalRouteUpdater(`DF-${selectedToken?.name}`, onDismiss)

  const [onPresent] = useModal(FiatCurrencySelectModal)
  const bonusControl = useWaitingDepositToActiveBonus()
  const { depositControl } = useDepositControl<DepositFiatControls>({
    selectedToken,
    mode: DepositModeEnums.FIAT,
  })
  const currencyInfo = useTokenInfo(selectedToken)

  useEffect(() => {
    if (!isSigned) return
    addMetaEvent('InitiateCheckout', {
      currency: initialToken
        ? `${initialToken.name}|${ChainIdEnum[initialToken.network]}`
        : selectedToken
        ? `${selectedToken.name}|${ChainIdEnum[selectedToken.network]}`
        : '',
    })
    fetchBonuses()
  }, [isSigned])

  const onPresentSelectToken = () => {
    onPresent({
      onTokenChange: (t) => {
        setSelectedToken(t)
      },
      selectedToken,
    })
  }

  useEffect(() => {
    if (!selectedToken) {
      setTimeout(() => {
        onPresentSelectToken()
      }, 500)
    }
  }, [])

  const handleClose = () => {
    setSelectedToken(null)
    onBack()
  }

  return (
    <StylePaymentModal sizeMode={ModalSizeModeEnum.Full} colorTheme={PAYMENT_MODAL_THEME} onDismiss={handleClose}>
      <ModalHeader
        useBackIcon
        hideClose
        hideStroke
        colorTheme={PAYMENT_MODAL_THEME}
        titleAlign="center"
        onBack={handleClose}
        onDismiss={handleClose}
        title={'Deposit Fiat'}
        suffixNode={<PaymentHistoryButton hideFindHash from={PaymentPageEnums.Deposit} />}
      />

      <ModalBody gap="var(--pm-space-component, 20px)">
        <Column gap="var(--pm-space-field, 8px)">
          <PaymentLabel>
            <Trans>Currency</Trans>
          </PaymentLabel>
          <SelectedCurrencyItem
            placeholder="Choose Currency"
            onPresentSelectCurrency={onPresentSelectToken}
            selectedToken={selectedToken}
          />
        </Column>

        {currencyInfo?.disabledDeposit ? (
          <PaymentCurrencyBlockMessage currency={selectedToken} page={PaymentPageEnums.Deposit} />
        ) : (
          <DepositFiatForm
            height="100%"
            selectedToken={selectedToken}
            bonusControl={bonusControl}
            depositControl={depositControl}
          />
        )}
      </ModalBody>
    </StylePaymentModal>
  )
}

export default DepositFiat
